import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { observer } from "mobx-react-lite";
import { ExportStore } from "../../librettoEditor/store/ExportStore";

/**
 * Thin circular progress indicator - matches the screenshot style
 * A clean, minimal ring that fills as progress increases
 */
const CircularProgress: React.FC<{ progress: number; size?: number }> = ({ progress, size = 36 }) => {
  const strokeWidth = 2.5;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <Box sx={{ position: "relative", width: size, height: size }}>
      {/* Background circle - very light */}
      <svg width={size} height={size} style={{ position: "absolute", top: 0, left: 0 }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#E5E7EB"
          strokeWidth={strokeWidth}
        />
      </svg>
      {/* Progress circle - dark, animates smoothly */}
      <svg 
        width={size} 
        height={size} 
        style={{ position: "absolute", top: 0, left: 0, transform: "rotate(-90deg)" }}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#1A1A1A"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 0.35s ease" }}
        />
      </svg>
    </Box>
  );
};

/**
 * Spinning indeterminate loader for "preparing" state
 * Thin, clean line that rotates smoothly
 */
const SpinningLoader: React.FC<{ size?: number }> = ({ size = 36 }) => {
  const strokeWidth = 2.5;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;

  return (
    <Box sx={{ position: "relative", width: size, height: size }}>
      <style>{`
        @keyframes export-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      <svg 
        width={size} 
        height={size} 
        style={{ 
          animation: "export-spin 1s linear infinite",
        }}
      >
        {/* Background track */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#E5E7EB"
          strokeWidth={strokeWidth}
        />
        {/* Spinning arc - partial circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#1A1A1A"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={circumference * 0.7}
          strokeLinecap="round"
        />
      </svg>
    </Box>
  );
};

interface ExportProgressCardProps {
  /** If true, renders as a floating card in the corner. If false, renders inline. */
  floating?: boolean;
}

/**
 * ExportProgressCard - Shows export progress with status
 * 
 * Design principles (from DesignSystem.txt):
 * - Calm, editorial, and content-first
 * - Soft neutral colors, generous whitespace
 * - Subtle elevation with soft shadows
 * - No playful or novelty animations
 * - This component will also be reused for uploads, transcription, etc.
 */
const ExportProgressCard: React.FC<ExportProgressCardProps> = observer(({ floating = true }) => {
  const { state, statusLabel, remainingTimeFormatted, progressPercent, isExporting, isCompleted, hasError } = ExportStore;
  const [displayedProgressPercent, setDisplayedProgressPercent] = React.useState(0);
  const [showEta, setShowEta] = React.useState(false);
  const displayedProgressRef = React.useRef(0);
  const rawProgressRef = React.useRef(0);
  const lastProgressPaintMsRef = React.useRef(0);
  const progressTimerRef = React.useRef<number | null>(null);

  const clearProgressTimer = React.useCallback(() => {
    if (progressTimerRef.current !== null) {
      window.clearTimeout(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  }, []);

  const commitDisplayedProgress = React.useCallback((nextProgress: number) => {
    const clamped = Math.max(0, Math.min(100, Math.round(nextProgress)));
    displayedProgressRef.current = clamped;
    setDisplayedProgressPercent(clamped);
    lastProgressPaintMsRef.current = Date.now();
  }, []);

  React.useEffect(() => {
    return () => {
      clearProgressTimer();
    };
  }, [clearProgressTimer]);

  React.useEffect(() => {
    rawProgressRef.current = progressPercent;
  }, [progressPercent]);

  React.useEffect(() => {
    if (state.status === 'idle' || state.status === 'preparing') {
      clearProgressTimer();
      displayedProgressRef.current = 0;
      rawProgressRef.current = 0;
      setDisplayedProgressPercent(0);
      setShowEta(false);
      lastProgressPaintMsRef.current = Date.now();
      return;
    }

    if (state.status === 'completed') {
      clearProgressTimer();
      commitDisplayedProgress(100);
      setShowEta(true);
      return;
    }

    if (state.status === 'error' || state.status === 'cancelled') {
      clearProgressTimer();
    }
  }, [clearProgressTimer, commitDisplayedProgress, state.status]);

  React.useEffect(() => {
    if (state.status !== 'exporting') {
      if (state.status !== 'completed') {
        setShowEta(false);
      }
      return;
    }
    setShowEta(progressPercent > 1);
  }, [progressPercent, state.status]);

  React.useEffect(() => {
    if (state.status !== 'exporting') {
      clearProgressTimer();
      return;
    }

    const raw = Math.max(0, Math.min(100, rawProgressRef.current));
    const shown = displayedProgressRef.current;

    if (raw >= 100) {
      clearProgressTimer();
      commitDisplayedProgress(100);
      return;
    }

    if (raw <= shown) {
      return;
    }

    const delta = raw - shown;
    if (delta < 7) {
      return;
    }

    const now = Date.now();
    const elapsedSinceLastPaint = now - lastProgressPaintMsRef.current;
    const waitMs = Math.max(0, 500 - elapsedSinceLastPaint);

    const flush = () => {
      const latestRaw = Math.max(0, Math.min(100, rawProgressRef.current));
      const latestShown = displayedProgressRef.current;
      if (latestRaw < 100 && latestRaw - latestShown < 7) {
        return;
      }
      commitDisplayedProgress(latestRaw);
    };

    if (waitMs === 0) {
      clearProgressTimer();
      flush();
      return;
    }

    clearProgressTimer();
    progressTimerRef.current = window.setTimeout(() => {
      progressTimerRef.current = null;
      flush();
    }, waitMs);
  }, [clearProgressTimer, commitDisplayedProgress, progressPercent, state.status]);

  // Don't render if idle
  if (state.status === 'idle') {
    return null;
  }

  const handleDismiss = () => {
    ExportStore.dismiss();
  };

  const handleCancel = () => {
    ExportStore.cancelExport();
  };

  const renderStatusIcon = () => {
    switch (state.status) {
      case 'preparing':
        return <SpinningLoader size={36} />;
      case 'exporting':
        return <CircularProgress progress={displayedProgressPercent} size={36} />;
      case 'completed':
        return (
          <Box sx={{ 
            width: 36, 
            height: 36, 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center" 
          }}>
            <CheckCircleIcon sx={{ fontSize: 28, color: "#22C55E" }} />
          </Box>
        );
      case 'error':
      case 'cancelled':
        return (
          <Box sx={{ 
            width: 36, 
            height: 36, 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center" 
          }}>
            <ErrorOutlineIcon 
              sx={{ 
                fontSize: 28, 
                color: state.status === 'error' ? "#EF4444" : "#9CA3AF",
              }} 
            />
          </Box>
        );
      default:
        return null;
    }
  };

  const getSubtitle = (): string => {
    if (state.status === 'preparing') {
      return 'Preparing...';
    }
    if (state.status === 'exporting') {
      if (!showEta) {
        return 'Preparing...';
      }
      return remainingTimeFormatted || 'Calculating...';
    }
    if (state.status === 'completed') {
      return 'Your file has been downloaded';
    }
    if (state.status === 'error') {
      return state.error || 'Something went wrong';
    }
    if (state.status === 'cancelled') {
      return 'Export was cancelled';
    }
    return '';
  };

  const cardContent = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.75,
        p: 1.75,
        pr: 2,
        minWidth: 300,
        fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
      }}
    >
      {/* Status icon / progress indicator */}
      <Box sx={{ flexShrink: 0 }}>
        {renderStatusIcon()}
      </Box>

      {/* Text content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography 
          sx={{ 
            fontSize: 14, 
            fontWeight: 600, 
            color: "#1A1A1A",
            lineHeight: 1.3,
            letterSpacing: "-0.01em",
          }}
        >
          {statusLabel}
        </Typography>
        <Typography 
          sx={{ 
            fontSize: 13, 
            color: "#6B7280",
            lineHeight: 1.35,
            mt: 0.25,
          }}
        >
          {getSubtitle()}
        </Typography>
      </Box>

      {/* Progress percentage or action button */}
      <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 0.5 }}>
        {state.status === 'exporting' && (
          <Typography 
            sx={{ 
              fontSize: 14, 
              fontWeight: 500, 
              color: "#6B7280",
              minWidth: 38,
              textAlign: "right",
            }}
          >
            {displayedProgressPercent}%
          </Typography>
        )}
        
        {/* Close/Cancel button */}
        {(isCompleted || hasError || state.status === 'cancelled') ? (
          <IconButton 
            size="small" 
            onClick={handleDismiss}
            sx={{ 
              color: "#9CA3AF",
              padding: "4px",
              "&:hover": { color: "#6B7280", backgroundColor: "#F3F4F5" },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        ) : isExporting || state.status === 'preparing' ? (
          <IconButton 
            size="small" 
            onClick={handleCancel}
            sx={{ 
              color: "#9CA3AF",
              padding: "4px",
              "&:hover": { color: "#EF4444", backgroundColor: "#FEF2F2" },
            }}
            title="Cancel export"
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        ) : null}
      </Box>
    </Box>
  );

  if (floating) {
    return (
      <Box
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          zIndex: 1400,
          backgroundColor: "#FFFFFF",
          borderRadius: "12px",
          boxShadow: "0px 20px 60px rgba(15, 23, 42, 0.12)",
          border: "1px solid #E5E7EB",
          overflow: "hidden",
        }}
      >
        {cardContent}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "12px",
        border: "1px solid #E5E7EB",
        overflow: "hidden",
      }}
    >
      {cardContent}
    </Box>
  );
});

export default ExportProgressCard;
